import React, { useEffect, useRef, useState } from 'react';
import './CustomSelect.css';

export type CustomSelectOption = {
  value: string;
  label: string;
};

export type CustomSelectProps = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: CustomSelectOption[];
  placeholder: string;
  disabled?: boolean;
  ariaLabelledBy?: string;
  className?: string;
  triggerClassName?: string;
  menuClassName?: string;
  optionClassName?: string;
};

export function CustomSelect({
  id,
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  ariaLabelledBy,
  className = '',
  triggerClassName = '',
  menuClassName = '',
  optionClassName = '',
}: CustomSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const selectedLabel = options.find((item) => item.value === value)?.label ?? placeholder;

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const pick = (nextValue: string) => {
    onChange(nextValue);
    setOpen(false);
  };

  return (
    <div className={`custom-select ${className}`.trim()} ref={rootRef}>
      <button
        type="button"
        id={id}
        className={`custom-select__trigger ${triggerClassName}${open ? ' is-open' : ''}${!value ? ' is-placeholder' : ''}`.trim()}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={ariaLabelledBy}
      >
        <span className="custom-select__value">{selectedLabel}</span>
      </button>
      {open && (
        <ul
          className={`custom-select__menu ${menuClassName}`.trim()}
          role="listbox"
          aria-labelledby={ariaLabelledBy}
        >
          <li
            role="option"
            aria-selected={value === ''}
            className={`custom-select__option ${optionClassName}${value === '' ? ' is-selected' : ''}`.trim()}
            onClick={() => pick('')}
          >
            {placeholder}
          </li>
          {options.map((item) => (
            <li
              key={item.value}
              role="option"
              aria-selected={value === item.value}
              className={`custom-select__option ${optionClassName}${value === item.value ? ' is-selected' : ''}`.trim()}
              onClick={() => pick(item.value)}
            >
              {item.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
